import { GoToCommentsMenuItem } from '@/components/comments/GoToCommentsButton/GoToCommentsButton';
import { StoreResellerBadge } from '@/components/StoreResellerBadge';
import Button from '@/design_system/Button';
import Menu from '@/design_system/Menu';
import { Section } from '@/design_system/Menu/Menu';
import Stack from '@/design_system/Stack';
import Paragraph from '@/design_system/Typography/Paragraph';
import Title from '@/design_system/Typography/Title';
import IconArrow from '@/icons/Arrow.svg';
import IconMore from '@/icons/More.svg';
import { getArticlePhoto, useArticleName } from '@/models/article';
import { ArticleWithRelations, RequestWithRelations, useComments } from '@/models/request';
import { useStore } from '@/models/store';
import { hasPermission, isExternalUserForOrganization } from '@/models/user';
import { useRequestContext } from '@/routes/Requests/contexts/RequestContext';
import { PrintItemSheetMenuItem } from '@/routes/Requests/Request/components/RequestHeader/PrintOptions';
import { useCurrentOrganization, useCurrentSession } from '@/services/auth';
import { getWorkshopsWithClientCommunication } from '@/utils/permission';
import { useRequestName } from '@/utils/useRequestName';
import useViewPort from '@/utils/useViewport';

import { useArticleContext } from '../../contexts/ArticleContext';
import { ClientCommunicationButton } from '../components/RequestHeader/ClientCommunicationButton';

export const ArticleHeader = ({
  request,
  article,
  onCommentButtonPress,
}: {
  request: RequestWithRelations;
  article: ArticleWithRelations;
  onCommentButtonPress: () => void;
}) => {
  const { isMobile } = useViewPort();
  const { currentSession } = useCurrentSession();
  const { view: requestView } = useRequestContext();
  const { view } = useArticleContext();
  const { data: store } = useStore(request?.storeId);
  const [currentOrganization] = useCurrentOrganization();
  const isExternalUser = isExternalUserForOrganization(currentSession, request.organization.id);

  const requestName = useRequestName({ request, isClientEnabled: requestView.client.enabled });
  const articleName = useArticleName({ article });
  const { data: { comments } = { comments: [] } } = useComments({
    requestId: request.id,
  });

  const hasViewClientCommunicationPermission = !!hasPermission(
    currentSession,
    'view_client_communication',
    [
      {
        organizationId: currentOrganization?.id,
        entityId: null,
      },
      {
        entityId: request.afterSalesId,
      },
      {
        entityId: request.store?.id,
      },
      ...getWorkshopsWithClientCommunication([article]),
    ]
  );

  const showClientCommunication =
    request.organization.config.clientCommunication?.chat?.enabled &&
    request.client &&
    hasViewClientCommunicationPermission;

  const showItemSheetPreview = view.itemSheet.preview.enabled;

  return (
    <Stack
      row
      alignItems="center"
      gap={isMobile ? '0.5rem' : undefined}
      style={{ flex: 1, minWidth: 0 }}
    >
      {isMobile && (
        <Stack row>
          <Button variant="secondary" size="large" to={`/requests/${request.id}`} iconOnly noBorder>
            <IconArrow left />
          </Button>

          {getArticlePhoto(article) && (
            <img
              src={getArticlePhoto(article)!.url}
              alt="article"
              style={{
                height: '40px',
                width: '40px',
                objectFit: 'contain',
                backgroundColor: 'var(--color-neutral-100)',
                borderRadius: '8px',
              }}
            />
          )}
        </Stack>
      )}

      <Stack gap={isMobile ? undefined : '0.25rem'} style={{ flex: 1, minWidth: 0 }}>
        {/* TODO: Migrate typography — uses headline-300-bold on desktop + paragraph-200-medium-mobile on mobile via CSS inheritance on a Stack with multiple children. Requires splitting into a Title with mobileSize support or restructuring the layout. */}
        <Stack
          row
          gap="0.5rem"
          alignItems="center"
          className="headline-300-bold sentry-mask paragraph-200-medium-mobile text-ellipsis"
        >
          [{request.reference}] {requestName.major}
          {!isExternalUser &&
            requestName.major === store?.name &&
            !!store?.organizationConfig?.external && <StoreResellerBadge size="large" />}
        </Stack>

        {isMobile && <Title size={isMobile ? '500' : '400'}>{articleName}</Title>}

        {!isMobile && !!requestName.minor && (
          <Stack row gap="0.25rem" alignItems="center">
            <Paragraph size="50">{requestName.minor}</Paragraph>
            {!isExternalUser &&
              requestName.minor === store?.name &&
              !!store?.organizationConfig?.external && <StoreResellerBadge />}
          </Stack>
        )}
      </Stack>

      {!isMobile && showClientCommunication && <ClientCommunicationButton />}

      {isMobile && (
        <Stack row alignItems="center">
          {showClientCommunication && <ClientCommunicationButton />}
          <Menu
            placement="bottom end"
            trigger={
              <Button variant="secondary" size="large" iconOnly noBorder>
                <IconMore />
              </Button>
            }
            onAction={(id) => {
              switch (id) {
                case 'go-to-comments':
                  onCommentButtonPress();
                  break;
                default:
                  break;
              }
            }}
          >
            <Section>
              <GoToCommentsMenuItem nbOfComments={comments.length} />
            </Section>
            {!!article.serviceChosenAt && !article.archivedAt && (
              <Section>
                <PrintItemSheetMenuItem article={article} />
              </Section>
            )}
            {showItemSheetPreview && (
              <Section>
                <PrintItemSheetMenuItem article={article} preview />
              </Section>
            )}
          </Menu>
        </Stack>
      )}
    </Stack>
  );
};
